import React, { Component } from 'react';
import { Button, Text, VStack, Center } from '@chakra-ui/react';

/**
 * Error boundary that catches ChunkLoadError (failed code-split chunk) and
 * shows a recovery UI with a reload button. Wraps the Suspense boundary
 * in App.js to prevent blank screens when chunk files are temporarily
 * unavailable (deploy cache mismatch, network flake).
 *
 * Note: React error boundaries can ONLY catch errors thrown during render,
 * lifecycle methods, and constructors. Chunk loading errors for CSS chunks
 * fire asynchronously (from `<link onerror>` events) and are NOT caught by
 * `componentDidCatch`. To handle those, this component also registers a
 * global `window.addEventListener('error')` handler for ChunkLoadError.
 */
class ChunkErrorBoundary extends Component {
  constructor (props) {
    super(props)
    this.state = { hasError: false, isReloading: false }
    this._globalErrorHandler = null
  }

  static getDerivedStateFromError () {
    return { hasError: true }
  }

  componentDidCatch (error) {
    this._handleChunkError(error)
  }

  /**
   * Register a global error listener for async chunk errors that React
   * error boundaries can't catch (e.g. CSS `<link>` onerror events).
   */
  componentDidMount () {
    this._globalErrorHandler = (event) => {
      // Filter: only handle ChunkLoadError
      if (event.error && event.error.name === 'ChunkLoadError') {
        event.preventDefault()
        console.warn('[CHUNK_ERROR] Global catch of async chunk error:', event.error.message)
        this._handleChunkError(event.error)
      }
    }
    window.addEventListener('error', this._globalErrorHandler)
  }

  componentWillUnmount () {
    if (this._globalErrorHandler) {
      window.removeEventListener('error', this._globalErrorHandler)
    }
  }

  _handleChunkError (error) {
    if (error.name === 'ChunkLoadError') {
      console.warn('[CHUNK_ERROR] Failed to load code-split chunk:', error.message)
      this.setState({ hasError: true })
    } else {
      console.error('[CHUNK_ERROR] Non-chunk error caught:', error)
    }
  }

  handleReload = () => {
    this.setState({ isReloading: true })
    window.location.reload()
  }

  render () {
    if (this.state.hasError) {
      return (
        <Center h="100vh" bg="gray.50">
          <VStack spacing={4} textAlign="center" px={4}>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              Something didn't load right
            </Text>
            <Text color="gray.500" maxW="400px">
              A page component couldn't be loaded. This can happen during
              deployments. A quick reload usually fixes it.
            </Text>
            <Button
              colorScheme="green"
              size="lg"
              onClick={this.handleReload}
              isLoading={this.state.isReloading}
              loadingText="Reloading..."
            >
              Reload Page
            </Button>
          </VStack>
        </Center>
      )
    }

    return this.props.children
  }
}

export default ChunkErrorBoundary